/**
 * PLP Banner with Collection Filters Component
 * 
 * Progressive enhancement for collection filter navigation.
 * Adds keyboard navigation, scroll tracking, and enhanced accessibility.
 * 
 * @class PlpBannerFilters
 * @extends HTMLElement
 */

import { defineElement } from './gw.js'

class PlpBannerFilters extends HTMLElement {
  constructor() {
    super()
    
    this.filtersNav = null
    this.filterLinks = []
    this.currentIndex = 0
    this.isCarouselEnabled = false
    
    // Bind methods
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.handleLinkClick = this.handleLinkClick.bind(this)
  }

  connectedCallback() {
    this.init()
  }

  disconnectedCallback() {
    this.destroy()
  }

  /**
   * Initialize the component
   */
  init() {
    // Find filters navigation element
    this.filtersNav = this.querySelector('[id^="PLPFilters-"]')
    if (!this.filtersNav) return

    // Check if carousel is enabled
    this.isCarouselEnabled = this.hasAttribute('enable-carousel') && 
                             this.filtersNav.classList.contains('slider')

    // Get all filter links
    this.filterLinks = Array.from(this.filtersNav.querySelectorAll('.plp-banner__filter-link'))
    if (this.filterLinks.length === 0) return

    // Find active link index
    const activeLink = this.filterLinks.find(link => link.classList.contains('plp-banner__filter-link--active'))
    if (activeLink) {
      this.currentIndex = this.filterLinks.indexOf(activeLink)
    }

    // Setup event listeners
    if (this.isCarouselEnabled) {
      this.filtersNav.addEventListener('scroll', this.handleScroll, { passive: true })
      this.setupKeyboardNavigation()
      this.scrollToActive()
    }

    // Setup analytics tracking
    this.setupAnalytics()

    // Setup ARIA live region updates
    this.setupAriaLiveRegion()
  }

  /**
   * Setup keyboard navigation for carousel
   * 
   * Enables keyboard navigation:
   * - Tab: Enter carousel region
   * - Arrow Left/Right: Navigate between links
   * - Home/End: Jump to first/last link
   * - Enter/Space: Select link (native link behavior)
   */
  setupKeyboardNavigation() {
    // Add keyboard event listener to nav container
    this.filtersNav.setAttribute('tabindex', '0')
    this.filtersNav.setAttribute('role', 'region')
    this.filtersNav.setAttribute('aria-label', 'Collection filters carousel')
    
    this.filtersNav.addEventListener('keydown', this.handleKeyDown)
  }

  /**
   * Handle keyboard navigation
   * 
   * Supports:
   * - ArrowLeft: Previous link
   * - ArrowRight: Next link
   * - Home: First link
   * - End: Last link
   * - Enter/Space: Native link activation (handled by browser)
   * 
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (!this.isCarouselEnabled || this.filterLinks.length === 0) return

    // Only handle arrow keys when nav is focused
    if (event.target !== this.filtersNav) return

    let handled = false

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault()
        this.navigateToLink(this.currentIndex - 1)
        handled = true
        break
      case 'ArrowRight':
        event.preventDefault()
        this.navigateToLink(this.currentIndex + 1)
        handled = true
        break
      case 'Home':
        event.preventDefault()
        this.navigateToLink(0)
        handled = true
        break
      case 'End':
        event.preventDefault()
        this.navigateToLink(this.filterLinks.length - 1)
        handled = true
        break
      // Enter and Space are handled natively by <a> elements
    }

    if (handled) {
      this.updateAriaLiveRegion()
    }
  }

  /**
   * Navigate to a specific link index
   * @param {number} index
   */
  navigateToLink(index) {
    if (index < 0) index = 0
    if (index >= this.filterLinks.length) index = this.filterLinks.length - 1

    this.currentIndex = index
    const targetLink = this.filterLinks[index]
    if (!targetLink) return

    // Scroll link into view
    targetLink.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    })

    // Update focus to the link
    targetLink.focus()

    // Dispatch analytics event
    this.dispatchAnalyticsEvent('keyboard_navigation', {
      index: index,
      collectionHandle: targetLink.dataset.collectionHandle,
      position: targetLink.dataset.position
    })
  }

  /**
   * Scroll active link into view on page load
   */
  scrollToActive() {
    const activeLink = this.filterLinks[this.currentIndex]
    if (!activeLink) return

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      activeLink.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      })
    })
  }

  /**
   * Handle scroll events to track current position
   * @param {Event} event
   */
  handleScroll(event) {
    if (!this.isCarouselEnabled) return

    // Find the link closest to the left edge of the viewport
    const navRect = this.filtersNav.getBoundingClientRect()
    let closestIndex = 0
    let closestDistance = Infinity

    this.filterLinks.forEach((link, index) => {
      const linkRect = link.getBoundingClientRect()
      const distance = Math.abs(linkRect.left - navRect.left)
      
      if (distance < closestDistance) {
        closestDistance = distance
        closestIndex = index
      }
    })

    if (closestIndex !== this.currentIndex) {
      this.currentIndex = closestIndex
      this.updateAriaLiveRegion()
    }
  }

  /**
   * Setup analytics tracking for link clicks
   */
  setupAnalytics() {
    this.filterLinks.forEach((link) => {
      link.addEventListener('click', this.handleLinkClick)
    })
  }

  /**
   * Handle link click for analytics
   * @param {MouseEvent} event
   */
  handleLinkClick(event) {
    const link = event.currentTarget
    this.dispatchAnalyticsEvent('filter_click', {
      collectionHandle: link.dataset.collectionHandle,
      position: link.dataset.position,
      href: link.href
    })
  }

  /**
   * Dispatch analytics event
   * @param {string} eventType
   * @param {Object} data
   */
  dispatchAnalyticsEvent(eventType, data) {
    const event = new CustomEvent('plpBannerFilterInteraction', {
      detail: {
        type: eventType,
        ...data
      },
      bubbles: true
    })
    this.dispatchEvent(event)
  }

  /**
   * Setup ARIA live region for screen reader announcements
   */
  setupAriaLiveRegion() {
    // Use existing aria-live on nav if available
    if (this.filtersNav && this.filtersNav.getAttribute('aria-live')) {
      this.ariaLiveElement = this.filtersNav
    } else if (this.isCarouselEnabled) {
      // Create a hidden live region if needed
      this.ariaLiveElement = document.createElement('div')
      this.ariaLiveElement.setAttribute('aria-live', 'polite')
      this.ariaLiveElement.setAttribute('aria-atomic', 'true')
      this.ariaLiveElement.className = 'visually-hidden'
      this.appendChild(this.ariaLiveElement)
    }
  }

  /**
   * Update ARIA live region with current position
   * Announces position to screen readers for better navigation context
   */
  updateAriaLiveRegion() {
    if (!this.ariaLiveElement || this.filterLinks.length === 0) return

    const total = this.filterLinks.length
    const current = this.currentIndex + 1
    const link = this.filterLinks[this.currentIndex]
    const linkText = link ? link.textContent?.trim() : ''
    const message = linkText 
      ? `${linkText}, filter ${current} of ${total}`
      : `Filter ${current} of ${total}`

    if (this.ariaLiveElement === this.filtersNav) {
      // Update aria-label with position info
      const baseLabel = this.filtersNav.getAttribute('aria-label') || 'Collection filters carousel'
      this.filtersNav.setAttribute('aria-label', `${baseLabel}, ${message}`)
    } else {
      this.ariaLiveElement.textContent = message
    }
  }

  /**
   * Cleanup event listeners
   */
  destroy() {
    if (this.filtersNav) {
      this.filtersNav.removeEventListener('scroll', this.handleScroll)
      this.filtersNav.removeEventListener('keydown', this.handleKeyDown)
    }

    this.filterLinks.forEach((link) => {
      link.removeEventListener('click', this.handleLinkClick)
    })

    if (this.ariaLiveElement && this.ariaLiveElement !== this.filtersNav) {
      this.ariaLiveElement.remove()
    }
  }
}

defineElement('plp-banner-filters', PlpBannerFilters)
